import { Injectable } from '@angular/core';
import {
	CarrierFeedback,
	Integration,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { AddOnSubscription } from '@state/session';

@Injectable({
	providedIn: 'root',
})
export class Cargochief {
	LOGIN_SETUP = 'loginSetup';

	constructor() {}

	get USER_ID(): number {
		const user: any = this.getUser();
		if (IS_NOT_NULL_OR_UNDEFINED(user)) {
			return user.id;
		}
		return null;
	}

	get ACCOUNT_ID(): number {
		const account: any = this.getAccount();
		if (IS_NOT_NULL_OR_UNDEFINED(account)) {
			return account.id;
		}
		return null;
	}

	get BUSINESS_NAME(): string {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.getAccount()) &&
			IS_NOT_NULL_OR_UNDEFINED(this.getAccount().businessName)
		) {
			return this.getAccount().businessName;
		}
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.getUser()) &&
			IS_NOT_NULL_OR_UNDEFINED(this.getUser().businessName)
		) {
			return this.getUser().businessName;
		}
		return null;
	}

	get USER_EMAIL(): string {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.getUser()) &&
			IS_NOT_NULL_OR_UNDEFINED(this.getUser().email)
		) {
			return this.getUser().email;
		}
		return null;
	}

	get IS_REGISTRATION_COMPLETED(): boolean {
		const account: any = this.getAccount();
		if (
			IS_NOT_NULL_OR_UNDEFINED(account) &&
			IS_NOT_NULL_OR_UNDEFINED(account.completedRegistration)
		) {
			return Boolean(JSON.parse(account.completedRegistration));
		}
		return true;
	}

	get USER_ACCOUNT_TYPE(): string {
		const account: any = this.getAccount();
		if (
			IS_NOT_NULL_OR_UNDEFINED(account) &&
			IS_NOT_NULL_OR_UNDEFINED(account.accountType) &&
			IS_NOT_EMPTY(account.accountType)
		) {
			return account.accountType.toUpperCase();
		}
		return '';
	}

	get MC(): string {
		const account: any = this.getAccount();
		if (
			IS_NOT_NULL_OR_UNDEFINED(account) &&
			IS_NOT_NULL_OR_UNDEFINED(account.accountType) &&
			IS_NOT_EMPTY(account.accountType)
		) {
			return account.mc;
		}
		return null;
	}

	get DOT(): string {
		const account: any = this.getAccount();
		if (
			IS_NOT_NULL_OR_UNDEFINED(account) &&
			IS_NOT_NULL_OR_UNDEFINED(account.accountType) &&
			IS_NOT_EMPTY(account.accountType)
		) {
			return account.dot;
		}
		return null;
	}

	get USER_NAME(): string {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.getUser()) &&
			(IS_NOT_EMPTY(this.getUser().firstName) || IS_NOT_EMPTY(this.getUser().lastName))
		) {
			return `${this.getUser().firstName} ${this.getUser().lastName}`;
		} else {
			return this.USER_EMAIL;
		}
	}

	get MY_CARRIER_PACKETS(): Integration {
		let value: any = this.getItem(CARGOCHIEF_KEYS.ACTIVE_INTEGRATIONS);
		if (IS_NOT_NULL_OR_UNDEFINED(value)) {
			let integrations = JSON.parse(value);
			integrations = Array.isArray(integrations) ? integrations : [integrations];
			const mcpData = integrations.find(
				(integration) => integration.integrationType === CARGOCHIEF_KEYS.MY_CARRIER_PACKETS
			);
			return mcpData;
		}
	}

	get HIGHWAY(): Integration {
		let value: any = this.getItem(CARGOCHIEF_KEYS.ACTIVE_INTEGRATIONS);
		if (IS_NOT_NULL_OR_UNDEFINED(value)) {
			let integrations = JSON.parse(value);
			integrations = Array.isArray(integrations) ? integrations : [integrations];
			const highwayData = integrations.find(
				(integration) => integration.integrationType === CARGOCHIEF_KEYS.HIGHWAY
			);
			return highwayData;
		}
	}

	get PLAN(): string {
		const account = this.getAccount();
		if (IS_NOT_NULL_OR_UNDEFINED(account) && IS_NOT_EMPTY(account.plan)) {
			return account.plan;
		}
		return null;
	}

	getIntegrationData(key: string): any {
		let value: any = this.getItem(CARGOCHIEF_KEYS.ACTIVE_INTEGRATIONS);
		if (IS_NOT_NULL_OR_UNDEFINED(value)) {
			let integrations = JSON.parse(value);
			integrations = Array.isArray(integrations) ? integrations : [integrations];
			const integrationData = integrations.find(
				(integration) => integration.integrationType === key
			);
			return integrationData;
		}
		return null;
	}

	get VETTING_INTEGRATION_TYPES(): string[] {
		return JSON.parse(this.getItem(CARGOCHIEF_KEYS.INTEGRATION_TYPES))
			?.filter((integration) => {
				return integration.category === 'VETTING';
			})
			.map((integration) => integration.name);
	}

	/**
	 * CN-8997: whether the account has an enabled TMS integration, so loads are arriving from a TMS.
	 *
	 * Comes straight from /loginSetup, which is stored before the app shell renders. It is NOT derived
	 * client-side from integration categories: that list lives in the INTEGRATION_TYPES cache, which
	 * logout clears and an async post-navigation effect repopulates — and the header builds its menu
	 * once, synchronously, in ngOnInit. Deriving it here hid the Loads tab for the whole first session
	 * after login, for exactly the accounts that need it.
	 *
	 * Absent (an older cached payload, or the server field missing) reads as false, matching the
	 * previous behaviour of not showing the tab.
	 */
	get HAS_ACTIVE_TMS_INTEGRATION(): boolean {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (!IS_NOT_NULL_OR_UNDEFINED(loginSetup) || !IS_NOT_EMPTY(loginSetup)) {
			return false;
		}

		try {
			return JSON.parse(loginSetup).hasActiveTmsIntegration === true;
		} catch (error) {
			console.warn('HAS_ACTIVE_TMS_INTEGRATION could not read loginSetup: ', error);
			return false;
		}
	}

	get ACTIVE_INTEGRATIONS(): boolean {
		let activeIntegrations: any = this.getItem(CARGOCHIEF_KEYS.ACTIVE_INTEGRATIONS);
		return IS_NOT_NULL_OR_UNDEFINED(activeIntegrations);
	}

	get HOME_PAGE(): string {
		let user: any = localStorage.getItem('loggedInUser');
		if (IS_NOT_NULL_OR_UNDEFINED(user) && IS_NOT_EMPTY(user)) {
			user = JSON.parse(user);
			if (IS_NOT_EMPTY(user.landingPage)) {
			}
			return user.landingPage;
		}
		return null;
	}

	get CARRIER(): any {
		let carrier: any = this.getItem(CARGOCHIEF_KEYS.CARRIER);
		if (IS_NOT_EMPTY(carrier)) {
			return JSON.parse(carrier);
		}
		return null;
	}

	get TERMS_AGREED(): any {
		const account: any = this.getAccount();
		if (IS_NOT_NULL_OR_UNDEFINED(account)) {
			return account.termsAgreedAt;
		}
		return null;
	}

	get IS_EMAIL_CONFIRMED(): boolean {
		const user: any = this.getUser();
		if (IS_NOT_NULL_OR_UNDEFINED(user) && IS_NOT_NULL_OR_UNDEFINED(user.emailConfirmed)) {
			try {
				return Boolean(JSON.parse(user.emailConfirmed));
			} catch (error) {
				console.warn('Failed to parse emailConfirmed field:', error);
				return false;
			}
		}
		return false;
	}

	getFirebaseToken(): string {
		return localStorage.getItem('afToken') || null;
	}

	getDeviceId(): string {
		return localStorage.getItem('deviceId') || null;
	}

	getAccount(): any {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (IS_NOT_NULL_OR_UNDEFINED(loginSetup) && IS_NOT_EMPTY(loginSetup)) {
			return JSON.parse(loginSetup).account;
		}
		return null;
	}

	getCarrierId(): any {
		return this.getAccount()?.carrierId;
	}

	getAccountSettings(): any {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (IS_NOT_NULL_OR_UNDEFINED(loginSetup) && IS_NOT_EMPTY(loginSetup)) {
			return JSON.parse(loginSetup).accountSettings;
		}
		return null;
	}

	getUserSettings(): any {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (IS_NOT_NULL_OR_UNDEFINED(loginSetup) && IS_NOT_EMPTY(loginSetup)) {
			return JSON.parse(loginSetup).userSettings;
		}
		return null;
	}

	getUser(): any {
		const user: string = localStorage.getItem('loggedInUser');
		if (IS_NOT_NULL_OR_UNDEFINED(user) && IS_NOT_EMPTY(user)) {
			return JSON.parse(user);
		}
		return null;
	}

	getActiveIntegrations(): any {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (IS_NOT_NULL_OR_UNDEFINED(loginSetup) && IS_NOT_EMPTY(loginSetup)) {
			return JSON.parse(loginSetup).activeIntegrations;
		}
		return null;
	}

	getTopSearches(): any {
		return JSON.parse(localStorage.getItem('topSearch')) || null;
	}

	getActiveOnly(): boolean {
		let activeOnly = true;
		if (localStorage.getItem(CARGOCHIEF_KEYS.ACTIVE_ONLY)) {
			activeOnly = JSON.parse(localStorage.getItem(CARGOCHIEF_KEYS.ACTIVE_ONLY));
		}
		return activeOnly;
	}

	setItem(jsonObject: { [keyName: string]: Object }): void {
		if (jsonObject) {
			let keys = Object.keys(jsonObject);
			if (keys && keys.length > 0) {
				keys.map((key) => {
					let value = jsonObject[key];
					localStorage.setItem(key, JSON.stringify(value));
				});
			}
		}
	}

	getAscendBidDetails() {
		return localStorage.getItem('ascendBidDetails');
	}

	getLayoutSetting() {
		return localStorage.getItem('layoutSetting');
	}

	setCustomerResponse(carrierId: string, carrierFeedback: CarrierFeedback) {
		let savedFeedbacks = localStorage.getItem(CARGOCHIEF_KEYS.CUSTOMER_FEEDBACK_LIST);
		if (savedFeedbacks != undefined && savedFeedbacks != null) {
			const feedbacks: CarrierFeedback[] = JSON.parse(savedFeedbacks);
			let matchIndex = -1;
			feedbacks.map((feedback: CarrierFeedback, index: number) => {
				if (feedback.carrierId == carrierId) {
					matchIndex = index;
				}
			});
			if (matchIndex > -1) {
				feedbacks[matchIndex] = carrierFeedback;
			} else {
				feedbacks.push(carrierFeedback);
			}
			this.setItem({ [CARGOCHIEF_KEYS.CUSTOMER_FEEDBACK_LIST]: feedbacks });
		} else {
			this.setItem({ [CARGOCHIEF_KEYS.CUSTOMER_FEEDBACK_LIST]: [carrierFeedback] });
		}
	}

	getCustomerResponseByCarrierId(carrierId: string): CarrierFeedback {
		let savedFeedbacks = localStorage.getItem(CARGOCHIEF_KEYS.CUSTOMER_FEEDBACK_LIST);
		if (savedFeedbacks != undefined && savedFeedbacks != null) {
			const feedbacks: CarrierFeedback[] = JSON.parse(savedFeedbacks);
			return feedbacks.filter((feedback) => {
				if (feedback.carrierId == carrierId) {
					return feedback;
				}
			})[0];
		}
		return null;
	}

	getCarrierFeedbackList(carrierId: string): CarrierFeedback[] {
		let savedFeedbacks = localStorage.getItem(CARGOCHIEF_KEYS.CARRIER_FEEDBACK_FROM_DB_LIST);
		if (savedFeedbacks != undefined && savedFeedbacks != null) {
			const feedbacksMapList = JSON.parse(savedFeedbacks);
			return feedbacksMapList[carrierId];
		}
		return null;
	}

	setCarrierFeedbackList(
		carrierId: string,
		carrierFeedbackList: CarrierFeedback[]
	): CarrierFeedback[] {
		let savedFeedbacks = localStorage.getItem(CARGOCHIEF_KEYS.CARRIER_FEEDBACK_FROM_DB_LIST);
		if (savedFeedbacks != undefined && savedFeedbacks != null) {
			const feedbacksMapList = JSON.parse(savedFeedbacks);
			feedbacksMapList[carrierId] = carrierFeedbackList;
			this.setItem({ [CARGOCHIEF_KEYS.CARRIER_FEEDBACK_FROM_DB_LIST]: feedbacksMapList });
		} else {
			this.setItem({
				[CARGOCHIEF_KEYS.CARRIER_FEEDBACK_FROM_DB_LIST]: { [carrierId]: carrierFeedbackList },
			});
		}
		return null;
	}

	getItem(key: string) {
		return localStorage.getItem(key);
	}

	removeItem(key: string) {
		localStorage.removeItem(key);
	}

	getPlanProducts(): any {
		if (
			localStorage.getItem('loginSetup') != undefined &&
			localStorage.getItem('loginSetup') != null
		) {
			const LOGIN_SETUP = JSON.parse(localStorage.getItem('loginSetup'));
			if (LOGIN_SETUP != undefined && LOGIN_SETUP != null) {
				return LOGIN_SETUP.planProducts;
			}
		}
		return null;
	}

	isProductEnabled(productName: string, planProducts?): boolean {
		let products;
		if (!!planProducts) {
			products = planProducts;
		} else {
			products = this.getPlanProducts();
		}

		if (products != null) {
			const product = products.filter((planProduct) => {
				if (planProduct?.product?.name == productName || planProduct?.productName == productName) {
					return planProduct;
				}
			});
			if (product.length > 0) {
				return product[0].enabled;
			}
		}
		return false;
	}

	getAddOnSubscriptions(): any {
		const LOGIN_SETUP = JSON.parse(localStorage.getItem(this.LOGIN_SETUP));
		if (LOGIN_SETUP) {
			return LOGIN_SETUP?.addOnSubscriptions;
		}
		return null;
	}

	getActiveAddOnSubscriptions(): any {
		let activeAddOnSubscriptions: AddOnSubscription[] = [];
		const addOnSubscriptions = this.getAddOnSubscriptions();
		if (IS_NOT_NULL_OR_UNDEFINED(addOnSubscriptions)) {
			activeAddOnSubscriptions = addOnSubscriptions.filter((subscription: AddOnSubscription) => {
				if (subscription?.expiresAt && !subscription.cancelledAt) {
					return true;
				}
				return false;
			});
		}
		return activeAddOnSubscriptions;
	}

	isAddOnSubscriptionValid(addOnProductName: string): boolean {
		let result = false;
		const addOnSubscriptions = this.getActiveAddOnSubscriptions();

		if (addOnSubscriptions?.length > 0) {
			addOnSubscriptions.forEach((subscription: AddOnSubscription) => {
				if (
					subscription?.addOnProduct?.products?.length > 0 &&
					subscription?.addOnProduct?.products
						?.toLowerCase()
						?.indexOf(addOnProductName?.toLowerCase()) !== -1
				) {
					result = true;
				}
			});
		}
		return result;
	}

	getLoginSetupUser(): any {
		return localStorage.getItem('loginSetup')
			? JSON.parse(localStorage.getItem('loginSetup'))['user']
			: null;
	}
}
